import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Grid } from "@/comn/components";
import { comnUtils } from "@/comn/utils"; // 프로젝트 공통 유틸
import { Page, Group, Layout, Button } from "@/comn/components"; // 화면 구성 컴포넌트
import { useForm, useFetch, useResource, useAuth, useGrid, useStore, useToast, usePage, useModal } from "@/comn/hooks"; // hook
import { URLS, BASE, APIS, SG_BLL_LIST, SF_BLL_LIST_SRCH } from "./services/OsciBllService"; // 서비스
import dayjs from "dayjs";
import { colUtils } from "@/tz/go/egaz/osci/colt/comn/";

/*
 * Bill List
 * !== 고지 목록 ==!
 */
export const OSCI0401001Q = (props: any) => {
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    useResource({
        defaultSchema: [{ area: "comnCd", comnCd: "OSC0056" }, { area: "comnCd", comnCd: "OSC0166" }, { area: "coCd" }],
    });

    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0003", "auth_osci0021"],
    }); // Page Unique identifier !== 화면 고유 식별자 ==!
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const auth = useAuth(); // Login , User Information Hook !== 로그인, 유저정보 Hook ==!
    let pgeTitle = "";

    if (menuId?.includes("coli")) {
        pgeTitle = "T_NTFC_LST";
    } else if (menuId?.includes("olmi")) {
        pgeTitle = "T_OIL_NTFC_LST";
    } else if (menuId?.includes("pcli")) {
        pgeTitle = "T_PSGR_NTFC_LST";
    }

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Bill Search !== 고지 검색 ==!
        bllSrch: useForm({
            defaultSchema: SF_BLL_LIST_SRCH,
            defaultValues: {
                ...(pgeStore?.form || {
                    bllTpCdSec: ["A", "C", "D", "E", "G", "P", "S", "T", "U", "W", "Y", "Z", "O", "V"],
                }),
            },
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        // Bill List !== 고지 목록 ==!
        bllList: useGrid({
            defaultSchema: SG_BLL_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Get Bill List !== 고지 목록 조회 ==!
        getBllList: useFetch({
            api: (page = grid.bllList.page) => {
                if (menuId?.includes("olmi")) {
                    form.bllSrch.setValue("oilDclrYn", "Y");
                }
                let data = {
                    ...form.bllSrch.getValues(),
                    orgCd: auth.get("orgCd"),
                };

                if (data.bllNo) {
                    data.bllNo = data.bllNo.trim(); // 앞뒤 공백 제거
                    // Bill No.가 "X-XXXX-XX-XXXXXXX-X"(bllMgmtId 마스크) 패턴이면 '-' 제거 후 조회
                    if (/^[A-Za-z0-9]-[A-Za-z0-9]{4}-[A-Za-z0-9]{2}-[A-Za-z0-9]{7}-[A-Za-z0-9]$/.test(data.bllNo)) {
                        data.bllNo = data.bllNo.replaceAll("-", "");
                    }
                }

                return APIS.getBllList(data, page, grid.bllList.size);
            },
            enabled: comnUtils.isEmpty(form.bllSrch.errors) && form.bllSrch.isSubmitted,
            key: [grid.bllList.page, grid.bllList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    form: form.bllSrch.getValues(),
                    page: grid.bllList.page,
                    size: grid.bllList.size,
                });
            },
            onError: () => {},
            showToast: true,
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Get Bill List !== 고지 목록 조회 ==!
        getBllList: (page: number = 0) => {
            // Validation Check !== 밸리데이션 체크 ==!
            if (!dateValidate()) {
                return false;
            }

            form.bllSrch.handleSubmit(
                (data) => {
                    grid.bllList.setPage(page);
                    fetch.getBllList.fetch(page);
                },
                (data) => {
                    toast.showToast({ type: "warning", content: "msg.osc.00003" });
                },
            )();
        },
    };

    const dateValidate = () => {
        const srchData = form.bllSrch.getValues();
        // const limit = 31;
        let result: boolean = true;

        // 고지일자
        // const bllDiffDay = dayjs(srchData.bllEndDt).diff(dayjs(srchData.bllStrtDt), "day"); // 조회기간(day, week, month, year)
        // if (bllDiffDay > limit) {
        //     modal.openModal({
        //         content: t("msg.osc.00183", { 0: t("L_BLL_DT"), 1: t("L_STRT_DT"), 2: limit }), // For Bill Date, please enter period between Start Date to 31 day.
        //     });
        //     result = false;
        // }

        // TANSAD 일자
        if (srchData.tansadStrtDt || srchData.tansadEndDt) {
            if (!srchData.tansadStrtDt) {
                modal.openModal({
                    content: t("msg.osc.00051", { 0: t("L_TANSAD") + " " + t("L_STRT_DT") }), // Please enter TANSAD Start Date.
                });
                result = false;
            } else if (!srchData.tansadEndDt) {
                modal.openModal({
                    content: t("msg.osc.00051", { 0: t("L_TANSAD") + " " + t("L_END_DT") }), // Please enter TANSAD End Date.
                });
                result = false;
                // } else {
                //     const tansadDiffDay = dayjs(srchData.tansadEndDt).diff(dayjs(srchData.tansadStrtDt), "day"); // 조회기간(day, week, month, year)
                //     if (tansadDiffDay > limit) {
                //         modal.openModal({
                //             content: t("msg.osc.00183", { 0: t("L_TANSAD_DT"), 1: t("L_STRT_DT"), 2: limit }), // For TANSAD Date, please enter period between Start Date to 31 day.
                //         });
                //         result = false;
                //     }
            }
        }
        return result;
    };

    const render = {
        grid_bllList: {
            head: {},
            cell: {
                bllNo: (props: any) => {
                    const { binding, rowValues, value } = props;
                    return (
                        <Layout direction="col" gap={1}>
                            <span style={{ color: "blue" }}>
                                <Link
                                    to={`${URLS.osci0401002q}/${rowValues.bllTpCd}-${rowValues.bllSysmCd}-${rowValues.bllYy}-${rowValues.bllSrno}-${rowValues.bllDgr}`}
                                >{`${rowValues.bllNo}`}</Link>
                            </span>
                        </Layout>
                    );
                },
                bllMgmtId: (props: any) => {
                    const { binding, rowValues, value } = props;
                    return (
                        <span>
                            {`${rowValues.bllTpCd}-${rowValues.bllSysmCd}-${rowValues.bllYy}-${rowValues.bllSrno}-${rowValues.bllDgr}`}
                        </span>
                    );
                },
                tansadNo: (props: any) => {
                    const { binding, rowValues, value } = props;

                    if (rowValues.tansadNo != null) {
                        console.log(rowValues.tansadNo.length);
                    }

                    if (rowValues.reffKeyTpCd == "T") {
                        return (
                            <Layout direction="col" gap={1}>
                                <span style={{ color: "blue" }}>
                                    <Link
                                        to={`${URLS.osci0401003q}/${rowValues.reffKeyNo1}-${rowValues.reffKeyNo2}-${rowValues.reffKeyNo3}`}
                                    >{`${rowValues.reffKeyNo1}-${rowValues.reffKeyNo2}-${rowValues.reffKeyNo3}`}</Link>
                                </span>
                            </Layout>
                        );
                    } else if (rowValues.tansadNo != null && rowValues.tansadNo.length > 6) {
                        const reffKeyNo1 = rowValues.tansadNo.substring(0, 4);
                        const reffKeyNo2 = rowValues.tansadNo.substring(4, 6);
                        const reffKeyNo3 = rowValues.tansadNo.substring(6);

                        console.log(`${reffKeyNo1}-${reffKeyNo2}-${reffKeyNo3}`);
                        return (
                            <Layout direction="col" gap={1}>
                                <span style={{ color: "blue" }}>
                                    <Link
                                        to={`${URLS.osci0401003q}/${reffKeyNo1}-${reffKeyNo2}-${reffKeyNo3}`}
                                    >{`${reffKeyNo1}-${reffKeyNo2}-${reffKeyNo3}`}</Link>
                                </span>
                            </Layout>
                        );
                    }
                },
                pyerMgmtNo: (props: any) => {
                    const { value, rowValues } = props;

                    return colUtils.setMask(value, "tin");
                },
            },
            edit: {},
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        handler.getBllList(pgeStore?.page);
    }, []);

    return (
        <Page title={t(pgeTitle)}>
            <form>
                <Group>
                    <Group.Body>
                        <Layout direction="row">
                            <Layout.Left size={6}>{}</Layout.Left>
                            <Layout.Right>{}</Layout.Right>
                        </Layout>
                        <Group.Section>
                            <Group.Row>
                                {/* Bill Custom Office Code !== 세관 코드 ==!  */}
                                <Group.Control {...form.bllSrch.schema.cstmOfceCd}></Group.Control>
                                {/* Payer Menagement Number !== 납부자 관리 번호 ==!  */}
                                <Group.Control {...form.bllSrch.schema.pyerMgmtNo}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                {/* Bill Number !== 고지서 번호 ==!  */}
                                <Group.Control {...form.bllSrch.schema.bllNo}></Group.Control>
                                <Group.Control {...form.bllSrch.schema.bllMgmtId}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                {/* Bill Date !== 고지서 일자 ==!  */}
                                <Group.Control {...form.bllSrch.schema.bllDtmRnge}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                {/* TANSAD Number !== TANSAD 번호 ==!  */}
                                <Group.Control {...form.bllSrch.schema.tansadNo}></Group.Control>
                                {/* GePG Control Number !== GePG 컨트롤 번호 ==!  */}
                                <Group.Control {...form.bllSrch.schema.ctrlNo}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                {/* TANSAD Date !== TANSAD 일자 ==!  */}
                                <Group.Control {...form.bllSrch.schema.tansadDtmRnge}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                {/* Bill State Code !== 고지서 상태 코드 ==!  */}
                                <Group.Control {...form.bllSrch.schema.bllStatCd}></Group.Control>
                                <Group.Control {...form.bllSrch.schema.appNo}></Group.Control>
                            </Group.Row>
                        </Group.Section>

                        <Layout direction="row">
                            <Layout.Left>
                                <Button
                                    role="reset"
                                    onClick={() => {
                                        form.bllSrch.reset();
                                    }}
                                ></Button>
                            </Layout.Left>
                            <Layout.Right>
                                <Button
                                    role="search"
                                    onClick={() => {
                                        handler.getBllList();
                                    }}
                                ></Button>
                            </Layout.Right>
                        </Layout>
                    </Group.Body>
                </Group>
            </form>

            <Group>
                <Group.Body>
                    <Layout direction="row">
                        <Layout.Left>
                            <Button
                                role="excelDown"
                                onClick={() => {
                                    grid.bllList.exportExcel({
                                        /* 다운로드 엑셀 파일명 (확장자 없이), 기본값 Export(년월일시분초) */
                                        excelName: dayjs().format("YYYY-MM-DD HH:mm:ss") + "_" + pgeUid,
                                        /* 다운로드 엑셀 시트명 , 기본값 Sheet */
                                        sheetName: "Sheet Name",
                                    });
                                }}
                            />
                            <Button
                                onClick={() => {
                                    form.bllSrch.handleSubmit(
                                        (data) => {
                                            // Validation Check !== 밸리데이션 체크 ==!
                                            if (!dateValidate()) {
                                                return false;
                                            }
                                            comnUtils.exportExcel({
                                                url: `${BASE.api}/bllExc`,
                                                data: { ...form.bllSrch.getValues(), orgCd: auth.get("orgCd") },
                                                fileName: "Collection Notice List",
                                            });
                                        },
                                        (data) => {
                                            toast.showToast({ type: "warning", content: "msg.osc.00003" });
                                        },
                                    )();
                                }}
                            >
                                {t("B_DTL_DWNL")}
                            </Button>
                        </Layout.Left>
                    </Layout>
                    <Grid {...grid.bllList.grid} data={fetch.getBllList.data?.bllList} render={render.grid_bllList} />
                </Group.Body>
            </Group>
            <Layout direction="row">
                <Layout.Left></Layout.Left>
            </Layout>
        </Page>
    );
};
